<div class="min-h-screen bg-base-100 text-base-content pt-12 pb-12">
  <div class="container mx-auto px-4 py-6">
    @if (isLoading) {
      <!-- Skeleton Header -->
      <div class="mb-6 space-y-2">
        <div class="daisy-skeleton h-9 w-64 rounded-lg"></div>
        <div class="daisy-skeleton h-5 w-48 rounded-lg"></div>
      </div>

      <!-- Skeleton Payment Method Selector -->
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div class="daisy-skeleton h-20 rounded-xl"></div>
        <div class="daisy-skeleton h-20 rounded-xl"></div>
        <div class="daisy-skeleton h-20 rounded-xl"></div>
      </div>

      <!-- Skeleton Main Content Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <!-- Left: Invoice Details Skeleton -->
        <div class="daisy-card daisy-card-bordered bg-base-100 shadow-lg p-6 space-y-4">
          <div class="daisy-skeleton h-20 w-full rounded-lg"></div>
          <div class="daisy-skeleton h-16 w-full rounded-lg"></div>
          <div class="space-y-3 pt-2">
            <div class="daisy-skeleton h-5 w-full rounded"></div>
            <div class="daisy-skeleton h-5 w-full rounded"></div>
            <div class="daisy-skeleton h-5 w-full rounded"></div>
            <div class="daisy-skeleton h-5 w-3/4 rounded"></div>
          </div>
        </div>

        <!-- Right: Payment Form Skeleton -->
        <div class="daisy-card daisy-card-bordered bg-base-100 shadow-lg p-6 space-y-4">
          <div class="daisy-skeleton h-7 w-40 rounded"></div>
          <div class="space-y-3">
            <div class="daisy-skeleton h-10 w-full rounded-lg"></div>
            <div class="daisy-skeleton h-10 w-full rounded-lg"></div>
            <div class="daisy-skeleton h-10 w-full rounded-lg"></div>
            <div class="daisy-skeleton h-24 w-full rounded-lg"></div>
            <div class="daisy-skeleton h-12 w-full rounded-lg"></div>
          </div>
        </div>
      </div>
    } @else {
      <!-- Header Section -->
      <div class="mb-6">
        <h1 class="text-3xl font-bold mb-2 text-base-content">Complete Your Payment</h1>
        <p class="text-base-content/70 text-base">Select a payment method to continue</p>
      </div>

      @if (!isPayHereEnabled && !isBankTransferEnabled) {
        <div class="daisy-alert daisy-alert-error mb-6">
          <i class="fa-solid fa-triangle-exclamation text-lg"></i>
          <span>Payment is currently unavailable. Please contact the academy administrator.</span>
        </div>
      }

    <!-- Payment Method Selector (always visible) -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
      <!-- Bank Transfer -->
      @if (isBankTransferEnabled) {
      <button
        type="button"
        class="relative flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all"
        [class.border-primary]="selectedPaymentMethod === 'bank'"
        [class.bg-primary/10]="selectedPaymentMethod === 'bank'"
        [class.shadow-md]="selectedPaymentMethod === 'bank'"
        [class.border-base-300]="selectedPaymentMethod !== 'bank'"
        [class.bg-base-100]="selectedPaymentMethod !== 'bank'"
        [class.hover:border-primary/50]="selectedPaymentMethod !== 'bank'"
        (click)="selectedPaymentMethod = 'bank'"
      >
        <div
          class="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
          [class.bg-primary]="selectedPaymentMethod === 'bank'"
          [class.text-primary-content]="selectedPaymentMethod === 'bank'"
          [class.bg-primary/10]="selectedPaymentMethod !== 'bank'"
          [class.text-primary]="selectedPaymentMethod !== 'bank'"
        >
          <i class="fa-solid fa-building-columns text-lg"></i>
        </div>
        <div class="min-w-0">
          <p
            class="font-semibold text-sm m-0"
            [class.text-primary]="selectedPaymentMethod === 'bank'"
            [class.text-base-content]="selectedPaymentMethod !== 'bank'"
          >
            Bank Transfer
          </p>
          <p class="text-xs text-base-content/60 m-0 mt-0.5">
            Transfer & upload receipt
          </p>
        </div>
        @if(selectedPaymentMethod === 'bank') {
        <div
          class="absolute top-2 right-2 w-5 h-5 bg-primary text-primary-content rounded-full flex items-center justify-center"
        >
          <i class="fa-solid fa-check text-xs"></i>
        </div>
        }
      </button>
      }

      <!-- PayHere Online Payment -->
      @if (isPayHereEnabled) {
      <button
        type="button"
        class="relative flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all"
        [class.border-primary]="selectedPaymentMethod === 'payhere'"
        [class.bg-primary/10]="selectedPaymentMethod === 'payhere'"
        [class.shadow-md]="selectedPaymentMethod === 'payhere'"
        [class.border-base-300]="selectedPaymentMethod !== 'payhere'"
        [class.bg-base-100]="selectedPaymentMethod !== 'payhere'"
        [class.hover:border-primary/50]="selectedPaymentMethod !== 'payhere'"
        (click)="selectedPaymentMethod = 'payhere'"
      >
        <div
          class="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
          [class.bg-primary]="selectedPaymentMethod === 'payhere'"
          [class.text-primary-content]="selectedPaymentMethod === 'payhere'"
          [class.bg-primary/10]="selectedPaymentMethod !== 'payhere'"
          [class.text-primary]="selectedPaymentMethod !== 'payhere'"
        >
          <i class="fa-solid fa-credit-card text-lg"></i>
        </div>
        <div class="min-w-0">
          <p
            class="font-semibold text-sm m-0"
            [class.text-primary]="selectedPaymentMethod === 'payhere'"
            [class.text-base-content]="selectedPaymentMethod !== 'payhere'"
          >
            PayHere
          </p>
          <p class="text-xs text-base-content/60 m-0 mt-0.5">
            Card / Online payment
          </p>
        </div>
        @if(selectedPaymentMethod === 'payhere') {
        <div
          class="absolute top-2 right-2 w-5 h-5 bg-primary text-primary-content rounded-full flex items-center justify-center"
        >
          <i class="fa-solid fa-check text-xs"></i>
        </div>
        }
      </button>
      }

      <!-- Online Wallet (disabled) -->
      <div
        class="relative flex items-center gap-4 p-4 rounded-xl border-2 border-base-300 bg-base-200/50 text-left opacity-50 cursor-not-allowed select-none"
        title="Coming soon"
      >
        <div
          class="w-11 h-11 rounded-xl bg-base-300 text-base-content/40 flex items-center justify-center flex-shrink-0"
        >
          <i class="fa-solid fa-wallet text-lg"></i>
        </div>
        <div class="min-w-0">
          <p class="font-semibold text-sm m-0 text-base-content/50">Online Wallet</p>
          <p class="text-xs text-base-content/40 m-0 mt-0.5">E-wallet payment</p>
        </div>
        <span
          class="absolute top-2 right-2 text-xs daisy-badge daisy-badge-ghost text-base-content/60 leading-tight"
          >Soon</span
        >
      </div>
    </div>

    <!-- Bank Transfer Flow -->
    @if(selectedPaymentMethod === 'bank') {

    <!-- Progress Steps Indicator -->
    <div class="mb-8">
      <div
        class="bg-primary/10 border-l-4 border-primary rounded-lg p-4 shadow-sm"
      >
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-primary font-semibold text-lg m-0">
            Payment Process
          </h4>
          <span class="text-sm text-primary/80 font-medium">3 Simple Steps</span>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="flex items-start space-x-3">
            <div
              class="flex-shrink-0 w-8 h-8 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold"
            >
              1
            </div>
            <div>
              <p class="font-semibold text-sm m-0 text-base-content">Review Invoice</p>
              <p class="text-xs text-base-content/60 m-0">Check amount and due date</p>
            </div>
          </div>
          <div class="flex items-start space-x-3">
            <div
              class="flex-shrink-0 w-8 h-8 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold"
            >
              2
            </div>
            <div>
              <p class="font-semibold text-sm m-0 text-base-content">Transfer Payment</p>
              <p class="text-xs text-base-content/60 m-0">Send to provided account</p>
            </div>
          </div>
          <div class="flex items-start space-x-3">
            <div
              class="flex-shrink-0 w-8 h-8 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold"
            >
              3
            </div>
            <div>
              <p class="font-semibold text-sm m-0 text-base-content">Upload Receipt</p>
              <p class="text-xs text-base-content/60 m-0">Attach payment proof</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <!-- Left Column: Invoice Details -->
      <div>
        <div class="flex items-center mb-4">
          <div
            class="w-8 h-8 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold mr-3 flex-shrink-0"
          >
            1
          </div>
          <h3 class="text-xl font-semibold m-0 text-base-content">Invoice Details</h3>
        </div>

        @if(currentInvoice){
        <div class="daisy-card daisy-card-bordered bg-base-100 shadow-lg">
          <div class="daisy-card-body p-6">
            <!-- Invoice Number - Highlighted -->
            <div class="bg-warning/10 border-2 border-warning/40 rounded-lg p-4 mb-4">
              <div class="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p class="text-xs text-warning font-semibold uppercase tracking-wide m-0 mb-1">Invoice Number</p>
                  <p class="text-xl font-bold text-base-content m-0 tracking-wider font-mono">
                    {{ currentInvoice.invoiceNumber }}
                  </p>
                </div>
                <div class="flex items-start gap-2 bg-warning/20 border border-warning/30 rounded-lg px-3 py-2 max-w-xs">
                  <i class="fa-solid fa-triangle-exclamation text-warning text-sm flex-shrink-0 mt-0.5"></i>
                  <p class="text-xs text-base-content/80 font-medium m-0">Include this number as the <strong>transaction reference</strong> when making your transfer</p>
                </div>
              </div>
            </div>

            <!-- Amount Due - Highlighted -->
            <div class="bg-error/10 border border-error/20 rounded-lg p-4 mb-4">
              <div class="flex justify-between items-center">
                <span class="text-base-content/80 font-semibold">Amount Due</span>
                <span class="text-2xl font-bold text-error">
                  {{
                    calculatePayable(
                      currentInvoice.amount,
                      currentInvoice.payments
                    )
                  }}
                  {{ currentInvoice.currency }}
                </span>
              </div>
              <div class="mt-2 text-sm text-base-content/70">
                <span class="font-medium">Due:</span>
                {{ currentInvoice.dueDate | date: 'longDate' }}
              </div>
            </div>

            <!-- Invoice Information -->
            <div class="space-y-3">
              <div class="flex justify-between py-2 border-b border-base-200">
                <span class="text-base-content/60">Invoice #</span>
                <span class="font-mono font-semibold text-base-content">{{ currentInvoice.invoiceNumber }}</span>
              </div>
              <div class="flex justify-between py-2 border-b border-base-200">
                <span class="text-base-content/60">Live Class</span>
                <span class="font-medium text-right text-base-content">{{
                  currentInvoice.subscription.liveClass.title
                }}</span>
              </div>
              <div class="flex justify-between py-2 border-b border-base-200">
                <span class="text-base-content/60">Start Date</span>
                <span class="font-medium text-base-content">{{
                  currentInvoice.subscription.liveClass.details.startDate
                    | date: 'longDate'
                }}</span>
              </div>
              <div class="flex justify-between py-2 border-b border-base-200">
                <span class="text-base-content/60">Total Amount</span>
                <span class="font-medium text-base-content"
                  >{{ currentInvoice.amount }}
                  {{ currentInvoice.currency }}</span
                >
              </div>
              <div class="flex justify-between py-2">
                <span class="text-base-content/60">Status</span>
                <span
                  class="daisy-badge"
                  [class.daisy-badge-success]="currentInvoice.status === 'PAID'"
                  [class.daisy-badge-warning]="
                    currentInvoice.status === 'PROCESSING' ||
                    currentInvoice.status === 'PARTIALY_PAID'
                  "
                  [class.daisy-badge-error]="
                    currentInvoice.status === 'OVERDUE' ||
                    currentInvoice.status === 'CANCELLED'
                  "
                  [class.daisy-badge-primary]="
                    currentInvoice.status !== 'PAID' &&
                    currentInvoice.status !== 'PROCESSING' &&
                    currentInvoice.status !== 'PARTIALY_PAID' &&
                    currentInvoice.status !== 'OVERDUE' &&
                    currentInvoice.status !== 'CANCELLED'
                  "
                >
                  {{ currentInvoice.status }}
                </span>
              </div>
            </div>
          </div>
        </div>
        }

        <!-- Quick Help Card -->
        <div class="mt-4 bg-base-200/60 border border-base-300 rounded-lg p-4">
          <h5 class="font-semibold text-sm mb-2 flex items-center text-base-content">
            <i class="fa-solid fa-circle-info mr-2 text-primary"></i>
            Important Notes
          </h5>
          <ul class="text-sm text-base-content/70 space-y-1 m-0 list-disc pl-5">
            <li>Include invoice reference in transfer notes</li>
            <li>Payment verified within 24 hours</li>
            <li>Accepted formats: PNG, JPG, PDF, Word, Excel</li>
          </ul>
        </div>
      </div>

      <!-- Right Column: Payment Form -->
      <div>
        <div class="flex items-center mb-4">
          <div
            class="w-8 h-8 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold mr-3 flex-shrink-0"
          >
            2
          </div>
          <h3 class="text-xl font-semibold m-0 text-base-content">Complete Payment</h3>
        </div>

        <div class="daisy-card daisy-card-bordered bg-base-100 shadow-lg">
          <div class="daisy-card-body p-6">
            <black-billing-form
              [bankAccounts]="tenantBankAccounts"
              (successPayment)="onPaymentSuccess($event)"
            ></black-billing-form>
          </div>
        </div>
      </div>
    </div>
    }

    <!-- PayHere Payment Flow -->
    @if(selectedPaymentMethod === 'payhere' && isPayHereEnabled) {
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <!-- Left Column: Invoice Details -->
      <div>
        <div class="flex items-center mb-4">
          <div
            class="w-8 h-8 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold mr-3 flex-shrink-0"
          >
            1
          </div>
          <h3 class="text-xl font-semibold m-0 text-base-content">Invoice Details</h3>
        </div>

        @if(currentInvoice){
        <div class="daisy-card daisy-card-bordered bg-base-100 shadow-lg">
          <div class="daisy-card-body p-6">
            <div class="bg-warning/10 border-2 border-warning/40 rounded-lg p-4 mb-4">
              <div class="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p class="text-xs text-warning font-semibold uppercase tracking-wide m-0 mb-1">Invoice Number</p>
                  <p class="text-xl font-bold text-base-content m-0 tracking-wider font-mono">
                    {{ currentInvoice.invoiceNumber }}
                  </p>
                </div>
              </div>
            </div>

            <div class="bg-error/10 border border-error/20 rounded-lg p-4 mb-4">
              <div class="flex justify-between items-center">
                <span class="text-base-content/80 font-semibold">Amount Due</span>
                <span class="text-2xl font-bold text-error">
                  {{
                    calculatePayable(
                      currentInvoice.amount,
                      currentInvoice.payments
                    )
                  }}
                  {{ currentInvoice.currency }}
                </span>
              </div>
              <div class="mt-2 text-sm text-base-content/70">
                <span class="font-medium">Due:</span>
                {{ currentInvoice.dueDate | date: 'longDate' }}
              </div>
            </div>

            <div class="space-y-3">
              <div class="flex justify-between py-2 border-b border-base-200">
                <span class="text-base-content/60">Invoice #</span>
                <span class="font-mono font-semibold text-base-content">{{ currentInvoice.invoiceNumber }}</span>
              </div>
              <div class="flex justify-between py-2 border-b border-base-200">
                <span class="text-base-content/60">Live Class</span>
                <span class="font-medium text-right text-base-content">{{
                  currentInvoice.subscription.liveClass.title
                }}</span>
              </div>
              <div class="flex justify-between py-2 border-b border-base-200">
                <span class="text-base-content/60">Start Date</span>
                <span class="font-medium text-base-content">{{
                  currentInvoice.subscription.liveClass.details.startDate
                    | date: 'longDate'
                }}</span>
              </div>
              <div class="flex justify-between py-2 border-b border-base-200">
                <span class="text-base-content/60">Total Amount</span>
                <span class="font-medium text-base-content"
                  >{{ currentInvoice.amount }}
                  {{ currentInvoice.currency }}</span
                >
              </div>
              <div class="flex justify-between py-2">
                <span class="text-base-content/60">Status</span>
                <span
                  class="daisy-badge"
                  [class.daisy-badge-success]="currentInvoice.status === 'PAID'"
                  [class.daisy-badge-warning]="
                    currentInvoice.status === 'PROCESSING' ||
                    currentInvoice.status === 'PARTIALY_PAID'
                  "
                  [class.daisy-badge-error]="
                    currentInvoice.status === 'OVERDUE' ||
                    currentInvoice.status === 'CANCELLED'
                  "
                  [class.daisy-badge-primary]="
                    currentInvoice.status !== 'PAID' &&
                    currentInvoice.status !== 'PROCESSING' &&
                    currentInvoice.status !== 'PARTIALY_PAID' &&
                    currentInvoice.status !== 'OVERDUE' &&
                    currentInvoice.status !== 'CANCELLED'
                  "
                >
                  {{ currentInvoice.status }}
                </span>
              </div>
            </div>
          </div>
        </div>
        }
      </div>

      <!-- Right Column: Billing Form & Pay Button -->
      <div>
        <div class="flex items-center mb-4">
          <div
            class="w-8 h-8 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold mr-3 flex-shrink-0"
          >
            2
          </div>
          <h3 class="text-xl font-semibold m-0 text-base-content">Billing Details</h3>
        </div>

        <div class="daisy-card daisy-card-bordered bg-base-100 shadow-lg">
          <div class="daisy-card-body p-6">
            @if(payhereError) {
            <div class="daisy-alert daisy-alert-error mb-4">
              <p class="text-sm m-0">{{ payhereError }}</p>
            </div>
            }

            <form [formGroup]="billingForm">
              <div class="grid grid-cols-1 gap-4">
                <div class="daisy-form-control">
                  <label class="daisy-label"><span class="daisy-label-text font-medium">Full Name</span></label>
                  <input type="text" formControlName="name" class="daisy-input daisy-input-bordered w-full" placeholder="Full Name" />
                  @if(billingForm.controls.name.touched && billingForm.controls.name.invalid) {
                  <span class="text-xs text-error mt-1">Name is required</span>
                  }
                </div>
                <div class="daisy-form-control">
                  <label class="daisy-label"><span class="daisy-label-text font-medium">Address Line 1</span></label>
                  <input type="text" formControlName="addressLine1" class="daisy-input daisy-input-bordered w-full" placeholder="Address Line 1" />
                  @if(billingForm.controls.addressLine1.touched && billingForm.controls.addressLine1.invalid) {
                  <span class="text-xs text-error mt-1">Address is required</span>
                  }
                </div>
                <div class="daisy-form-control">
                  <label class="daisy-label"><span class="daisy-label-text font-medium">Address Line 2</span></label>
                  <input type="text" formControlName="addressLine2" class="daisy-input daisy-input-bordered w-full" placeholder="Address Line 2" />
                  @if(billingForm.controls.addressLine2.touched && billingForm.controls.addressLine2.invalid) {
                  <span class="text-xs text-error mt-1">Address Line 2 is required</span>
                  }
                </div>
                <div class="grid grid-cols-2 gap-4">
                  <div class="daisy-form-control">
                    <label class="daisy-label"><span class="daisy-label-text font-medium">City</span></label>
                    <input type="text" formControlName="city" class="daisy-input daisy-input-bordered w-full" placeholder="City" />
                    @if(billingForm.controls.city.touched && billingForm.controls.city.invalid) {
                    <span class="text-xs text-error mt-1">City is required</span>
                    }
                  </div>
                  <div class="daisy-form-control">
                    <label class="daisy-label"><span class="daisy-label-text font-medium">Province</span></label>
                    <input type="text" formControlName="province" class="daisy-input daisy-input-bordered w-full" placeholder="Province" />
                    @if(billingForm.controls.province.touched && billingForm.controls.province.invalid) {
                    <span class="text-xs text-error mt-1">Province is required</span>
                    }
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-4">
                  <div class="daisy-form-control">
                    <label class="daisy-label"><span class="daisy-label-text font-medium">Postal Code</span></label>
                    <input type="text" formControlName="postal" class="daisy-input daisy-input-bordered w-full" placeholder="Postal Code" />
                    @if(billingForm.controls.postal.touched && billingForm.controls.postal.invalid) {
                    <span class="text-xs text-error mt-1">Postal code is required</span>
                    }
                  </div>
                  <div class="daisy-form-control">
                    <label class="daisy-label"><span class="daisy-label-text font-medium">Country</span></label>
                    <input type="text" formControlName="country" class="daisy-input daisy-input-bordered w-full" placeholder="Country" />
                    @if(billingForm.controls.country.touched && billingForm.controls.country.invalid) {
                    <span class="text-xs text-error mt-1">Country is required</span>
                    }
                  </div>
                </div>
                <div class="daisy-form-control">
                  <label class="daisy-label"><span class="daisy-label-text font-medium">Contact Number</span></label>
                  <input type="text" formControlName="contact" class="daisy-input daisy-input-bordered w-full" placeholder="Contact Number" />
                  @if(billingForm.controls.contact.touched && billingForm.controls.contact.invalid) {
                  <span class="text-xs text-error mt-1">Contact number is required</span>
                  }
                </div>
              </div>

              <button
                type="button"
                class="daisy-btn daisy-btn-primary w-full mt-6"
                (click)="processPayherePayment()"
              >
                Pay with PayHere
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
    }
    }
  </div>
</div>
